import { useEffect, useState } from 'react';
import { Avatar, Card, Col, Empty, Row, Tag, Timeline, Typography } from 'antd';
import { UsergroupAddOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { fetchConnections, Connection } from '../api/network';

const { Title, Text } = Typography;

export default function Activity() {
  const { token } = useAuth();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetchConnections(token).then(setConnections).finally(() => setLoading(false));
  }, [token]);

  const sorted = [...connections].sort((a, b) =>
    new Date(b.connected_at ?? 0).getTime() - new Date(a.connected_at ?? 0).getTime()
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Activity Feed</Title>
        <Text type="secondary">{connections.length} connections</Text>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>Loading activity…</div>
      ) : connections.length === 0 ? (
        <Empty
          image={<UsergroupAddOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description="No connections yet — connect with businesses to see activity here."
        />
      ) : (
        <Row gutter={[24, 0]}>
          <Col xs={24} md={14}>
            <Card title="Recent Connections">
              <Timeline
                items={sorted.map(conn => ({
                  dot: conn.logo_url
                    ? <Avatar size={28} src={conn.logo_url} />
                    : <Avatar size={28} icon={<UserOutlined />} style={{ background: '#722ed1' }} />,
                  children: (
                    <div>
                      <Text strong>{conn.company_name}</Text>
                      <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>{conn.company_type.replace('_', ' ')}</Tag>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {conn.city ? `${conn.city} · ` : ''}
                        Connected {conn.connected_at ? new Date(conn.connected_at).toLocaleDateString() : 'recently'}
                      </Text>
                    </div>
                  ),
                }))}
              />
            </Card>
          </Col>
          <Col xs={24} md={10}>
            <Card title="Network Stats">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ textAlign: 'center', padding: 16, background: '#f9f0ff', borderRadius: 8 }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#722ed1' }}>{connections.length}</div>
                  <div style={{ color: '#999', fontSize: 13 }}>Business Connections</div>
                </div>
                <div style={{ textAlign: 'center', padding: 16, background: '#f6ffed', borderRadius: 8 }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#52c41a' }}>
                    {new Set(connections.map(c => c.city).filter(Boolean)).size}
                  </div>
                  <div style={{ color: '#999', fontSize: 13 }}>Cities Represented</div>
                </div>
                <div style={{ textAlign: 'center', padding: 16, background: '#e6f4ff', borderRadius: 8 }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#1677ff' }}>
                    {new Set(connections.map(c => c.company_type)).size}
                  </div>
                  <div style={{ color: '#999', fontSize: 13 }}>Business Categories</div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}
